import { lazy, Suspense, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, IconButton, Popover, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';

// The emoji-mart picker plus its dataset and German labels are loaded as an
// async chunk the first time the popover opens.
const LazyPicker = lazy(async () => {
  const [pickerModule, dataModule, i18nModule] = await Promise.all([
    import('@emoji-mart/react'),
    import('@emoji-mart/data'),
    import('@emoji-mart/data/i18n/de.json'),
  ]);
  const Picker = pickerModule.default;
  const data = dataModule.default;
  const i18n = i18nModule.default;
  const WrappedPicker = props => <Picker data={data} i18n={i18n} {...props} />;
  return { default: WrappedPicker };
});

// Emoji button for the chat composer: opens a full emoji picker and reports
// the chosen emoji (native character) via onSelect.
export default function EmojiPickerButton({ onSelect, disabled }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const handleSelect = emoji => {
    setAnchorEl(null);
    if (emoji?.native) onSelect(emoji.native);
  };

  return (
    <>
      <Tooltip title='Emoji einfügen'>
        <span>
          <IconButton
            onClick={e => setAnchorEl(e.currentTarget)}
            disabled={disabled}
            aria-label='Emoji einfügen'
          >
            <EmojiEmotionsOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Suspense
          fallback={
            <Box
              sx={{
                width: 352,
                height: 435,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          }
        >
          <LazyPicker
            onEmojiSelect={handleSelect}
            theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
            previewPosition='none'
            skinTonePosition='search'
            autoFocus
          />
        </Suspense>
      </Popover>
    </>
  );
}

EmojiPickerButton.propTypes = {
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
